import React, { useState, useCallback, useEffect } from "react"; // useMemo,
import { useTranslator } from "context/translator";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";

import Switch from "components/switchButton/Switch";



/**
 * Props esperadas:
 * - subModulos: Array<SubModulo>
 * - setSubModulos: (updaterFn) => void  // típico setState
 * - onDirty?: () => void               // opcional: avisar "cambiosPendientesPermiso = true"
 *
 * SubModulo:
 * { _id, name, allow, showPermisos, permissions: Permission[] }
 *
 * Permission:
 * { _id, name, allow, dependencies: Permission[] }
 */

export default function PermissionsPanel({ openP, subModulos, setSubModulos, disabledPermissions, onDirty }) {

    const { translate } = useTranslator();

    const [permisoTodos, setPermisoTodos] = useState(false);
    const [showCode, SetShowCode] = useState(false);

    // ------- Helpers puros (inmutables) -------
    const setAllDown = useCallback((permissions, allow) => {
        return (permissions || []).map((p) => ({
            ...p,
            allow,
            dependencies: p.dependencies?.length ? setAllDown(p.dependencies, allow) : p.dependencies,
        }));
    }, []);

    const anyTrueInTree = useCallback((permissions) => {
        for (const p of permissions || []) {
            if (p.allow) return true;
            if (p.dependencies?.length && anyTrueInTree(p.dependencies)) return true;
        }
        return false;
    }, [/* recursive via closure */]);

    /**
     * Actualiza un árbol de permissions:
     * - si newValue=true: marca el target y todos los ancestorsIds en true
     * - si newValue=false: apaga target y apaga TODO hacia abajo (sus dependencies)
     * - no marca siblings
     */
    const updatePermissionTree = useCallback(
        (permissions, targetId, newValue, ancestorsIdsSet) => {
            return (permissions || []).map((p) => {
                const isAncestor = ancestorsIdsSet?.has(p._id);
                const isTarget = p._id === targetId;

                // Recorremos hijos siempre
                let newDependencies = p.dependencies?.length
                    ? updatePermissionTree(p.dependencies, targetId, newValue, ancestorsIdsSet)
                    : p.dependencies;

                // Regla de "marcar hacia arriba" cuando newValue=true
                // (marcamos el target y sus ancestros)
                if (newValue === true) {
                    if (isTarget || isAncestor) {
                        return {
                            ...p,
                            allow: true,
                            dependencies: newDependencies,
                        };
                    }
                    return { ...p, dependencies: newDependencies };
                }

                // newValue === false:
                // - solo afecta al target (y hacia abajo)
                if (isTarget) {
                    const apagadas = p.dependencies?.length ? setAllDown(p.dependencies, false) : p.dependencies;
                    return {
                        ...p,
                        allow: false,
                        dependencies: apagadas,
                    };
                }

                // resto: solo conserva cambios que vinieron desde más abajo
                return { ...p, dependencies: newDependencies };
            });
        },
        [setAllDown]
    );

    // ------- Acciones UI -------
    const toggleShowAll = useCallback((value) => {
        setSubModulos((prev) =>
            prev.map((sm) => ({
                ...sm,
                showPermissions: value,
            }))
        );
    }, [setSubModulos]);

    const toggleAll = useCallback(() => {
        const next = !permisoTodos;
        setPermisoTodos(next);

        setSubModulos((prev) =>
            prev.map((sm) => ({
                ...sm,
                allow: next,
                permissions: setAllDown(sm.permissions, next),
            }))
        );

        onDirty?.();
    }, [permisoTodos, setAllDown, setSubModulos, onDirty]);

    const toggleSubModulo = useCallback((subModuloId) => {
        setSubModulos((prev) =>
            prev.map((sm) => {
                if (sm._id !== subModuloId) return sm;

                const next = !sm.allow;
                return {
                    ...sm,
                    allow: next,
                    permissions: setAllDown(sm.permissions, next),
                };
            })
        );

        onDirty?.();
    }, [setSubModulos, setAllDown, onDirty]);

    const toggleShowSubModulo = useCallback((subModuloId) => {
        setSubModulos((prev) =>
            prev.map((sm) =>
                sm._id === subModuloId ? { ...sm, showPermissions: !sm.showPermissions } : sm
            )
        );
    }, [setSubModulos]);

    /**
     * node: el permiso clickeado
     * ancestors: array de permissions ancestros (desde el primer permiso del submodulo hasta el padre directo)
     */
    const togglePermiso = useCallback((subModuloId, node, ancestors) => {
        const newValue = !node.allow;

        const ancestorsIdsSet = new Set((ancestors || []).map((a) => a._id));

        setSubModulos((prev) =>
            prev.map((sm) => {
                if (sm._id !== subModuloId) return sm;

                const updatedPermissions = updatePermissionTree(
                    sm.permissions,
                    node._id,
                    newValue,
                    // si enciende => marcamos ancestros; si apaga => no importa ancestros
                    newValue ? ancestorsIdsSet : null
                );

                // Sub-módulo:
                // - si newValue=true => siempre debe quedar true
                // - si newValue=false => recalcular si hay algún true en TODO el árbol
                const subAllow = newValue ? true : anyTrueInTree(updatedPermissions);

                return {
                    ...sm,
                    allow: subAllow,
                    permissions: updatedPermissions,
                };
            })
        );

        onDirty?.();
    }, [setSubModulos, updatePermissionTree, anyTrueInTree, onDirty]);

    // INIT al abrir modal y mostrar permisos
    useEffect(() => {

        if (openP) {

            toggleShowAll(false);
            //setPermisoTodos(false);
            SetShowCode(true);

        } else {

            SetShowCode(false);

        }

    }, [openP, toggleShowAll])

    // ------- Render -------
    return (

        <>

        {showCode && (

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }} className="text-gray-900 dark:text-white">
                {/* Barra superior: Todos + mostrar/ocultar */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", paddingBottom: 14 }}>
                    <label style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, fontSize: "22px" }}>


                        {/*<input*/}
                        {/*    type="checkbox"*/}
                        {/*    checked={permisoTodos}*/}
                        {/*    onChange={toggleAll}*/}
                        {/*/>*/}

                            <Switch
                                value={permisoTodos}
                                onChange={() => toggleAll()}
                                disabled={disabledPermissions}
                        />


                        <span> {translate("all")} </span>
                    </label>
                    &nbsp;
                    <button type="button" onClick={() => toggleShowAll(false)} title={translate("hideAllPermissions")}
                        className="border px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-black">

                        <ArrowUpOutlined />

                    </button>
                    <button type="button" onClick={() => toggleShowAll(true)} title={translate("showAllPermissions")}
                        className="border px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-black">

                        <ArrowDownOutlined />

                    </button>
                </div>



                {/* Sub-módulos */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {(subModulos || []).map((sm) => (
                        <div key={sm._id} style={{ paddingBottom: "12px", paddingTop: "12px", paddingRight: "12px" }}>
                            {/* Header sub-módulo */}
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <button type="button" onClick={() => toggleShowSubModulo(sm._id)} style={{ fontSize: "20px", verticalAlign: "center" }} title={sm.showPermissions ? translate("hidePermissions") : translate("showPermissions")} >

                                    {sm.showPermissions ? (
                                        <div>
                                            <ArrowUpOutlined />
                                        </div>
                                    ) : (
                                        <div>
                                            <ArrowDownOutlined />
                                        </div>
                                    )}

                                </button>

                                &nbsp;

                                <div>

                                    <label style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10 }}>



                                        <span style={{ fontSize: 18, fontWeight: 700 }}> {translate(sm.name)} </span>

                                        {/*<input*/}
                                        {/*    type="checkbox"*/}
                                        {/*    checked={!!sm.allow}*/}
                                        {/*    onChange={() => toggleSubModulo(sm._id)}*/}
                                        {/*/>*/}

                                        <Switch
                                            value={!!sm.allow}
                                            onChange={() => toggleSubModulo(sm._id)}
                                            size="sm"
                                            disabled={disabledPermissions}
                                        />


                                    </label>

                                </div>
                            </div>

                            {/* Lista permissions (solo si showPermissions) */}
                            {sm.showPermissions && (
                                <div style={{ marginTop: 10 }}>
                                    <PermissionList
                                        permissions={sm.permissions}
                                        subModuloId={sm._id}
                                        onToggle={togglePermiso}
                                        ancestors={[]}
                                        level={1}
                                        disabledPermissions={disabledPermissions}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

        )}

        </>
    );
}

/** Render recursivo infinito */
function PermissionList({ permissions, subModuloId, onToggle, ancestors, level, disabledPermissions }) {

    const { translate } = useTranslator();

    if (!permissions?.length) return null;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 10 }}>
            {permissions.map((p) => (
                <div key={p._id}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            marginLeft: level * 25,
                        }}
                    >
                        <label style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>


                            {/*<input*/}
                            {/*    type="checkbox"*/}
                            {/*    checked={!!p.allow}*/}
                            {/*    onChange={() => onToggle(subModuloId, p, ancestors)}*/}
                            {/*/>*/}


                            <Switch
                                value={!!p.allow}
                                onChange={() => onToggle(subModuloId, p, ancestors)}
                                size="xs"
                                disabled={disabledPermissions}
                            />

                            <span> {translate(p.name)} </span>
                        </label>
                    </div>

                    {/* dependencies */}
                    {p.dependencies?.length ? (
                        <PermissionList
                            permissions={p.dependencies}
                            subModuloId={subModuloId}
                            onToggle={onToggle}
                            ancestors={[...ancestors, p]} // p ahora es ancestro para sus hijos
                            level={level + 1}
                            disabledPermissions={disabledPermissions}
                        />
                    ) : null}
                </div>
            ))}
        </div>
    );
}